/* ⚠️ GENERATED from app/services/selfIdentity.ts by gen-twins.mjs — edit the TypeScript, not this. */
/* ═══════════════════════════════════════════════════════════════════════════
   services/selfIdentity.ts — who I am, answered without a network.

   ── 🟥 REPORTED ────────────────────────────────────────────────────────────
   "if I have no internet and I go to profile it will show user instead of my
    name and no profile pic" … "I saw the profile pic on both".

   ── 🟥 TWO FAILURES, ONE SCREEN ────────────────────────────────────────────
   THE NAME. There IS an offline copy of the profile — HomeScreen writes one to
   AsyncStorage — but it is stored under `user_profile_cache_<uid>` and read by
   an effect that begins `if (!currentUser) return;`. So the only copy of my own
   name is behind a door whose key is the thing that may not have arrived yet,
   and the write that fills it is deferred inside
   `InteractionManager.runAfterInteractions` — a callback a launch is allowed to
   drop. A record that is hard to read and easy not to write is not a cache.

   THE PICTURE. `userProfile.profileImage` is an https:// URL on Firebase
   Storage, drawn by a plain <Image source={{ uri }} />. With no network there
   is nothing to fetch. The HTTP image caches both platforms keep are
   best-effort, size-limited and evicted by the OS whenever it likes, which is
   exactly why this reproduces on iOS AND Android and why it looked like a
   platform difference until it was tried on both. No amount of choosing between
   <Image> and expo-image fixes a picture that is not on the device.

   🟢 SO THE ANSWER IS KEPT, NOT FETCHED. One small record under one key that
      does not mention a uid, so it can be read on the first frame; and a real
      file on disk for the face, so "draw my picture" never needs a radio.

   🟥 AND AN EMPTY STRING IS AN ANSWER. `''` means "I removed my picture" and
      must be allowed to clear a stale one; `undefined` means "I have not heard"
      and must never overwrite anything. That is the same distinction
      services/peerAvatar.ts had to learn for other people's faces — the bug
      there was a removal that never propagated, and it would reappear here in
      mirror image: a picture deleted online, still on this phone, redrawn from
      disk forever.

   Pure on purpose: no AsyncStorage, no FileSystem, no fetch. The phone's half
   lives in services/selfIdentityStore.ts and the browser's in
   allo-web/src/services/selfIdentityWeb.js, and both ask this file the
   questions. That is what lets it be a twin.
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * One key, no uid in it — readable before Firebase Auth has rehydrated.
 *
 * 🟥 THE `allo_` PREFIX IS NOT DECORATION. The browser wipes an account off a
 *    shared machine by a KEEP-list, not a wipe-list — everything matching
 *    /^allo[_:]/ goes on sign-out unless it is named as a machine preference
 *    (allo-web/src/services/wipeLocal.js says why at length). A record holding
 *    my name and the URL of my face under a key outside that pattern would be
 *    left behind for the next person, and nothing would ever notice.
 */
export const SELF_IDENTITY_KEY = 'allo_self_identity_v1';
/* ── presence, not truthiness ──────────────────────────────────────────────
   🟥 `profile?.profileImage || fallback` IS THE BUG THIS FILE EXISTS TO AVOID.
      It cannot tell a profile that has not loaded from one that says "no
      picture", and it resolves both the same way — by reaching for something
      older. Ask whether the field is THERE. */
function present(o, k) {
  return !!o && typeof o === 'object' && k in o && o[k] !== undefined;
}
function text(v) {
  return typeof v === 'string' ? v.trim() : v == null ? '' : String(v).trim();
}

/** The first source that actually carries the field, or undefined if none does. */
export function pickField(k, ...sources) {
  for (const s of sources) if (present(s, k)) return text(s[k]);
  return undefined;
}

/* ── the record ────────────────────────────────────────────────────────────── */

/**
 * Build the record from whatever is in hand.
 *
 * `profile` may be null (nothing loaded yet) — then the result is `partial` and
 * its blanks are "don't know", which `mergeIdentity` respects.
 */
export function identityFrom(uid, profile, authUser, now) {
  if (!uid) return null;
  const loaded = !!profile && typeof profile === 'object';
  const photo = pickField('profileImage', profile) ?? pickField('photoURL', authUser) ?? '';
  return {
    uid,
    fullName: pickField('fullName', profile) ?? text(authUser?.displayName),
    username: pickField('username', profile) ?? '',
    photo,
    file: '',
    partial: !loaded,
    at: typeof now === 'number' ? now : Date.now()
  };
}

/**
 * Fold a new reading into the stored one.
 *
 * 🟥 A PARTIAL READING MAY NOT ERASE. The sign-in handler knows my uid and my
 *    auth displayName and nothing else; if that overwrote the record, every
 *    launch would throw away the username and the picture it had been keeping
 *    for exactly this moment — the cold offline start — and the bug would come
 *    back wearing the fix's clothes.
 *
 * 🟢 A FULL READING MAY. A profile document that came back with no fullName is
 *    an account with no fullName.
 *
 * 🟥 THE FILE FOLLOWS THE URL IT WAS MADE FROM. Keep it only while the picture
 *    it is a copy of is still the picture. A changed avatar with a kept file is
 *    yesterday's face drawn with total confidence.
 */
export function mergeIdentity(prev, next) {
  if (!next) return prev;
  if (!prev || prev.uid !== next.uid) return next;
  /* 🟥 "MAY NOT ERASE" WAS NOT ENOUGH — A PARTIAL READING MAY NOT REPLACE EITHER.
     The auth user carries a `photoURL` too, and it is NOT the picture: the
     profile screen writes `profileImage` on the user document and leaves the
     auth field pointing wherever it pointed when the account was made (or at a
     file since deleted). The sign-in reading runs on EVERY launch, so every
     launch swapped my real picture for that stale URL — and, because the file
     follows its URL, threw away the copy on disk with it. Offline, my own tile
     in every group collage was blank until the network brought the profile
     back. A full reading is the truth; a partial one only fills in a record
     that has never had one. */
  const keep = (a, b) => !next.partial ? a : !prev.partial ? b : a || b;
  const photo = keep(next.photo, prev.photo);
  return {
    uid: next.uid,
    fullName: keep(next.fullName, prev.fullName),
    username: keep(next.username, prev.username),
    photo,
    file: photo && photo === prev.photo ? prev.file : '',
    partial: next.partial && prev.partial,
    at: next.at
  };
}

/** Attach the local copy — only if it is a copy of the picture we are holding. */
export function withIdentityFile(id, photo, file) {
  if (!id) return id;
  if (!photo || id.photo !== photo) return id; // the picture moved on while we downloaded
  if (id.file === file) return id;
  return {
    ...id,
    file
  };
}

/** Is this record worth writing over that one? Identical records cost a disk write for nothing. */
export function identitySame(a, b) {
  if (!a || !b) return a === b;
  return a.uid === b.uid && a.fullName === b.fullName && a.username === b.username && a.photo === b.photo && a.file === b.file && a.partial === b.partial;
}

/** Parse what came off disk. Anything unrecognisable is "we have nothing". */
export function parseIdentity(raw) {
  let v = raw;
  if (typeof raw === 'string') {
    try {
      v = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!v || typeof v !== 'object' || typeof v.uid !== 'string' || !v.uid) return null;
  return {
    uid: v.uid,
    fullName: text(v.fullName),
    username: text(v.username),
    photo: text(v.photo),
    file: text(v.file),
    partial: v.partial === true,
    at: Number(v.at) || 0
  };
}

/** The record for a uid, or null if what we kept belongs to somebody else. */
export function identityForUid(id, uid) {
  if (!id) return null;
  if (uid && id.uid !== uid) return null;
  return id;
}

/* ── what the screen draws ─────────────────────────────────────────────────── */

/**
 * Every name this account is known by, in the order the rest of the app already
 * prefers them — and the kept record BEFORE the literal, because "User" is not
 * a name, it is the app admitting it forgot.
 */
export function selfDisplayName(profile, authUser, id, fallback) {
  return text(profile?.fullName) || text(profile?.username) || text(authUser?.displayName) || text(id?.fullName) || text(id?.username) || fallback;
}
export function selfUsername(profile, id, fallback) {
  return text(profile?.username) || text(id?.username) || fallback;
}

/**
 * What to hand an <Image>/<img>, or null for the placeholder.
 *
 * 🟢 THE LOCAL COPY WINS WHENEVER IT IS A COPY OF THE CURRENT PICTURE — online
 *    too. A file that is already on this phone cannot fail to load, cannot
 *    flash, and costs no bytes; there is no version of "fetch it again over the
 *    network" that is better, only versions that are worse offline.
 */
export function selfAvatarSource(profile, authUser, id) {
  const remote = pickField('profileImage', profile) ?? pickField('photoURL', authUser) ?? text(id?.photo);
  if (!remote) return null; // '' — removed, or never set
  if (id && id.file && id.photo === remote) return id.file; // the copy on this device
  return remote;
}

/** Should the store go and fetch a copy? Only for a picture it does not already have. */
export function avatarNeedsCache(id, remote) {
  const url = text(remote);
  if (!url || !/^https?:\/\//i.test(url)) return false; // a file:// we already made is not a job
  if (!id) return true;
  return !(id.file && id.photo === url);
}

/**
 * A stable file name for a URL.
 *
 * 🟥 NOT `profile.jpg`. Every avatar this account ever had lives at the same
 *    Storage path — `profileImages/<uid>/profile.jpg` — and differs only by the
 *    download token in the query string. One fixed local name means a changed
 *    picture writes over the old file at a moment the app may be reading it,
 *    and any reader holding the path can no longer tell which face it has.
 *    Hash the whole URL: a new picture is a new file, and the old one becomes
 *    garbage that names itself.
 */
export function avatarFileName(url) {
  let h = 5381;
  const s = text(url);
  for (let i = 0; i < s.length; i += 1) h = (h * 33 ^ s.charCodeAt(i)) >>> 0;
  return `self_avatar_${h.toString(36)}.jpg`;
}

/** Old avatar files for this account — everything that is not the one in use. */
export function staleAvatarFiles(names, keep) {
  const k = text(keep);
  return (names || []).filter(n => /^self_avatar_[0-9a-z]+\.jpg$/.test(n) && n !== k);
}
